/* eslint-disable */
import React, { useCallback, useState, useEffect } from "react";
import { Mail, Lock, Eye, EyeOff, ArrowRight, AlertCircle, Ban, Plug } from "lucide-react";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import jwtDecode from "jwt-decode";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Login as LoginApi, LoginWithGoogle } from "../../api/auth";
import { showToast } from "../../utils/showToast";
import { AuthService } from "../../services/authservice";
import { getConfig } from "../../api/configService";
import { resolveLoginDestination } from "../../utils/loginRedirect";
import { peekConnectReturn, resolvePostLoginTarget } from "../../utils/connectReturn";
import { useGoogleSignIn } from "../../hooks/useGoogleSignIn";
import { useTheme } from "../../providers/ThemeProvider";
import BeartrackLogo from "../../assets/beartrack/beartrack_logo.png";
import LoginAside from "./LoginAside";
import ThemeToggle from "../../components/themeToggle";

export default function Login() {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const methods = useForm({ mode: "onSubmit" });
  const [loading, setLoading] = useState(false);
  const [paymentBlocked, setPaymentBlocked] = useState(false);
  // Si el servidor cerró la sesión por bloqueo de cuenta (en el arranque o en medio del uso),
  // el motivo llega acá tras la recarga a /login. No se borra al montar: el cierre suele
  // pasar por /login dos veces (navegación del router y luego la recarga de expireSession).
  // Se borra al iniciar sesión con éxito.
  const [sessionEndReason] = useState(() => AuthService.getSessionEndReason());
  // La tienda trajo al comercio a autorizar una conexión y no tenía sesión: se le
  // avisa con qué cuenta entrar, porque al terminar vuelve a esa autorización.
  const [pendingConnectReturn] = useState(() => peekConnectReturn() !== null);
  const [showPassword, setShowPassword] = useState(false);

  // DvlprChris
  const [config, setConfig] = useState(null);
  const [storedColor, setStoredColor] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const serviceConfig = await getConfig();
        setConfig(serviceConfig);
        setStoredColor(serviceConfig.color);
        localStorage.setItem("appConfig", JSON.stringify(serviceConfig));
        setConfigLoaded(true);
      } catch (error) {
      }
    };
    fetchConfig();
  }, []);

  // Aterrizaje común de los dos caminos de autenticación (contraseña y Google): decide el
  // portal según el rol del accessToken. El destino por rol vive en utils/loginRedirect
  // para poder testearlo sin montar la vista.
  const applyLoginDestination = useCallback((accessToken) => {
    AuthService.clearSessionEndReason();
    let userData;
    try {
      userData = jwtDecode(accessToken);
    } catch (error) {
      console.error("No se pudo decodificar el accessToken:", error);
      showToast(t("No se pudo completar el inicio de sesión. Intenta nuevamente."), false);
      setLoading(false);
      return;
    }

    // Cuenta de comercio con una autorización de tienda pendiente: vuelve a ella
    // en vez de a /home (ver utils/connectReturn).
    const target = resolvePostLoginTarget(resolveLoginDestination(userData));

    if (!target) {
      showToast(
        t("De momento solo pueden ingresar usuarios de tipo cliente o administrador."),
        false
      );
      setLoading(false);
      return;
    }

    showToast(t("Login exitoso"), true);
    setLoading(false);
    window.location.replace(target);
  }, [t]);

  const submitValues = async (values) => {
      setLoading(true);
      const { data, error } = await LoginApi(values);
      if (error) {
        if (error.code === 'PAYMENT_BLOCKED') {
          setPaymentBlocked(true);
          setLoading(false);
          return;
        }
        showToast(error.message, false);
        setLoading(false);
        return;
      }

      if (data.message !== "El usuario no se encuentra registrado.") {
        applyLoginDestination(data?.accessToken);
      }}

  // Recibe el id_token que emite Google Identity Services y lo canjea por una sesión.
  const handleGoogleCredential = useCallback(async (idToken) => {
    setLoading(true);
    const { data, error } = await LoginWithGoogle(idToken);

    if (error) {
      setLoading(false);

      if (error.code === "PAYMENT_BLOCKED") {
        setPaymentBlocked(true);
        return;
      }

      // El backend no crea cuentas desde la web: los usuarios del portal se dan de alta
      // por invitación, así que un correo desconocido se informa y no dispara registro.
      if (error.code === "NEEDS_REGISTRATION") {
        showToast(
          t("No existe una cuenta de Beartrack asociada a ese correo. Solicita el acceso a tu administrador."),
          false
        );
        return;
      }

      if (error.code === "WEB_ACCESS_NOT_ALLOWED") {
        showToast(
          t("Tu cuenta no tiene acceso al portal web. Si eres repartidor, ingresa desde la aplicación."),
          false
        );
        return;
      }

      if (error.code === "GOOGLE_LOGIN_NOT_ALLOWED") {
        showToast(
          t("Esta cuenta no puede iniciar sesión con Google. Ingresa con tu correo y contraseña."),
          false
        );
        return;
      }

      showToast(error.message || t("No se pudo iniciar sesión con Google."), false);
      return;
    }

    applyLoginDestination(data?.accessToken);
  }, [applyLoginDestination, t]);

  // Sin Client ID configurado el hook queda en `disabled` y no se dibuja el botón: es
  // preferible no ofrecer Google a mostrar un botón que falla.
  const { containerRef: googleButtonRef, status: googleStatus } = useGoogleSignIn({
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    onCredential: handleGoogleCredential,
    isDark: theme === "dark",
    locale: i18n?.language,
  });

  const emailError = methods?.formState?.errors?.email;
  const passwordError = methods?.formState?.errors?.password;

  return (
    <>
    <Modal
      open={paymentBlocked}
      onClose={() => setPaymentBlocked(false)}
      size="sm"
      ariaLabel={t("Cuenta Suspendida")}
      footer={
        <Button
          variant="outline"
          fullWidth
          onClick={() => setPaymentBlocked(false)}
        >
          {t("Volver al inicio de sesión")}
        </Button>
      }
    >
      <div className="flex flex-col items-center text-center gap-3 py-2">
        <span className="flex items-center justify-center w-14 h-14 rounded-full bg-status-error-soft text-status-error">
          <Ban size={28} aria-hidden="true" />
        </span>
        <h2 className="text-lg font-semibold text-fg">{t("Cuenta Suspendida")}</h2>
        <p className="text-sm text-fg-muted leading-relaxed">
          {t("Su cuenta se encuentra")}{" "}
          <strong className="font-semibold text-fg">{t("inactiva por falta de pago")}</strong>.
          {t(" Por favor contáctese con administración para regularizar su situación.")}
        </p>
        <p className="text-sm text-fg-muted">
          {t("Si cree que esto es un error, escriba a")}{" "}
          <a
            href="mailto:emorales@grupogamu.cl"
            className="font-medium text-brand-500 hover:text-brand-600"
          >
            emorales@grupogamu.cl
          </a>
        </p>
      </div>
    </Modal>

    <div className="login-split">
      {/* Permite elegir claro/oscuro desde el propio login (igual que el TopBar
          interno). El tema se persiste en localStorage vía ThemeProvider. */}
      <ThemeToggle className="login-theme-toggle" />
      <LoginAside />
      <main className="login-main">
        <div className="login-main__inner">
          <div className="login-logo-container login-logo-container--mobile">
            <img src={BeartrackLogo} alt="BearTrack" className="login-logo" />
          </div>

          <div className="login-header">
            <h1 className="login-title">{t("Bienvenido de vuelta")}</h1>
            <p className="login-subtitle">{t("Ingresa a tu panel de operación.")}</p>
          </div>

                {sessionEndReason === "blocked" && (
                  <div
                    role="alert"
                    className="mb-4 flex items-start gap-2 rounded-lg border border-status-error/40 bg-status-error-soft p-3 text-sm text-status-error"
                  >
                    <Ban className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{t("Tu cuenta fue bloqueada. Contacta a tu administrador.")}</span>
                  </div>
                )}
                {pendingConnectReturn && (
                  <div
                    role="status"
                    className="mb-4 flex items-start gap-2 rounded-lg border border-status-info/40 bg-status-info-soft p-3 text-sm text-status-info"
                  >
                    <Plug className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
                    <span>
                      {t("Inicia sesión con tu cuenta de comercio para terminar de conectar tu tienda.")}{" "}
                      {t("¿No tienes cuenta? Pídesela a tu operador logístico.")}
                    </span>
                  </div>
                )}
                <form onSubmit={methods.handleSubmit(submitValues)} className="login-form" noValidate>
                  <div className="login-form-group">
                    <div className="login-field">
                      <label htmlFor="login-email" className="login-label">{t("Correo electrónico")}</label>
                      <div className="login-input-wrap">
                        <span className="login-input-icon"><Mail size={18} strokeWidth={2} /></span>
                        <Controller
                          name="email"
                          control={methods.control}
                          rules={{ required: t("Ingrese su correo") }}
                          render={({ field }) => (
                            <input
                              {...field}
                              id="login-email"
                              type="email"
                              autoComplete="email"
                              className="login-input"
                              placeholder={t("tucorreo@gmail.com")}
                              aria-invalid={emailError ? "true" : "false"}
                              onChange={(e) => field.onChange(e.target.value.toLowerCase())}
                            />
                          )}
                        />
                      </div>
                      {emailError && (
                        <div className="login-error" role="alert">
                          <AlertCircle size={14} />
                          {emailError.message}
                        </div>
                      )}
                    </div>

                    <div className="login-field">
                      <label htmlFor="login-password" className="login-label">{t("Contraseña")}</label>
                      <div className="login-input-wrap">
                        <span className="login-input-icon"><Lock size={18} strokeWidth={2} /></span>
                        <Controller
                          name="password"
                          control={methods.control}
                          rules={{ required: t("Ingrese su contraseña") }}
                          render={({ field }) => (
                            <input
                              {...field}
                              id="login-password"
                              type={showPassword ? "text" : "password"}
                              autoComplete="current-password"
                              className="login-input"
                              placeholder="••••••••"
                              aria-invalid={passwordError ? "true" : "false"}
                            />
                          )}
                        />
                        <button
                          type="button"
                          className="login-toggle"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label={showPassword ? t("Ocultar contraseña") : t("Mostrar contraseña")}
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                      {passwordError && (
                        <div className="login-error" role="alert">
                          <AlertCircle size={14} />
                          {passwordError.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="login-actions">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={loading}
                      iconRight={<ArrowRight size={18} />}
                      className={`login-button ${loading ? 'loading' : ''}`}
                    >
                      {loading ? t("Iniciando sesión…") : t("Iniciar sesión")}
                    </Button>

                    {googleStatus !== "disabled" && (
                      <>
                        <div className="login-divider"><span>{t("o continúa con")}</span></div>

                        {/* El botón lo dibuja Google dentro de un iframe (ver
                            useGoogleSignIn): no se puede deshabilitar con el atributo
                            `disabled`, así que durante el login se bloquea por CSS. */}
                        <div
                          className={`login-google-slot ${loading ? "is-busy" : ""}`}
                          aria-busy={loading ? "true" : "false"}
                        >
                          <div ref={googleButtonRef} />
                        </div>

                        {googleStatus === "error" && (
                          <p className="login-google-error" role="alert">
                            {t("No se pudo cargar el inicio de sesión con Google. Ingresa con tu correo y contraseña.")}
                          </p>
                        )}
                      </>
                    )}

                    <div className="login-forgot">
                      <a href="/login/recovery" className="login-forgot-link">
                        {t("¿Olvidaste tu contraseña?")}
                      </a>
                    </div>
                  </div>
                </form>
        </div>
      </main>
    </div>
    </>
  );
}
